import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Select } from "@/components/ui/Select";
import {
  useCreateKnowledgeEntry,
  useDeleteKnowledgeAttachment,
  useDivisionsPicker,
  useDraftKnowledgeEntry,
  useInvestorsPicker,
  useProjectsPicker,
  useUpdateKnowledgeEntry,
  useUploadKnowledgeAttachments,
} from "../api/engAronApi";
import type {
  DocType,
  EngBranch,
  KnowledgeEntry,
  KnowledgeEntryPayload,
  KnowledgeKind,
  KnowledgeMeta,
  KnowledgeScope,
  ReviewCategory,
} from "../types";

interface Props {
  open: boolean;
  onClose: () => void;
  meta: KnowledgeMeta;
  /** Wpis do edycji; null = nowy wpis. */
  entry: KnowledgeEntry | null;
  defaultKind: KnowledgeKind;
}

export function KnowledgeEntryFormModal({ open, onClose, meta, entry, defaultKind }: Props) {
  const [kind, setKind] = useState<KnowledgeKind>(defaultKind);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState<ReviewCategory>("terminology");
  const [scope, setScope] = useState<KnowledgeScope>("global");
  const [branch, setBranch] = useState<EngBranch | "">("");
  const [investor, setInvestor] = useState<string>("");
  // Oddział/Rejon cascade — `divisionInvestor` scopes the division picker; it is a
  // helper for the cascade (not persisted for division/subdivision scopes).
  const [divisionInvestor, setDivisionInvestor] = useState<string>("");
  const [clientBranch, setClientBranch] = useState<string>("");
  const [clientSubBranch, setClientSubBranch] = useState<string>("");
  const [project, setProject] = useState<string>("");
  const [docTypes, setDocTypes] = useState<DocType[]>([]);
  const [tagsText, setTagsText] = useState("");
  const [why, setWhy] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  const projects = useProjectsPicker("");
  const investors = useInvestorsPicker("");
  const divisions = useDivisionsPicker(divisionInvestor);

  const createEntry = useCreateKnowledgeEntry();
  const updateEntry = useUpdateKnowledgeEntry();
  const uploadAttachments = useUploadKnowledgeAttachments();
  const deleteAttachment = useDeleteKnowledgeAttachment();
  const draftEntry = useDraftKnowledgeEntry();

  // Wklej screena ze schowka (Ctrl+V) wprost w treść — obraz ląduje w screenach,
  // tekst wkleja się normalnie.
  function handlePasteImages(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const imgs: File[] = [];
    for (const item of Array.from(e.clipboardData.items)) {
      if (item.kind === "file" && item.type.startsWith("image/")) {
        const f = item.getAsFile();
        if (!f) continue;
        const ext = item.type.split("/")[1] || "png";
        imgs.push(
          new File([f], `wklejony-${Date.now()}-${imgs.length}.${ext}`, { type: item.type }),
        );
      }
    }
    if (imgs.length === 0) return; // brak obrazu → zwykłe wklejenie tekstu
    e.preventDefault();
    setFiles((prev) => [...prev, ...imgs]);
  }

  async function handleDraft() {
    setError(null);
    if (!body.trim() && files.length === 0) {
      setError("Wklej opis (w pole treści) albo dodaj screeny — z tego AI przygotuje wpis.");
      return;
    }
    try {
      const d = await draftEntry.mutateAsync({ description: body.trim(), files });
      setKind(d.kind);
      setTitle(d.title);
      setBody(d.body);
      setCategory(d.category);
      setDocTypes(d.doc_types ?? []);
      setTagsText((d.tags ?? []).join(", "));
      if (d.why) setWhy(d.why);
    } catch {
      setError("Nie udało się przygotować szkicu — spróbuj ponownie.");
    }
  }

  useEffect(() => {
    if (!open) return;
    setError(null);
    setFiles([]);
    if (entry) {
      setKind(entry.kind);
      setTitle(entry.title);
      setBody(entry.body);
      setCategory(entry.category);
      setScope(entry.scope);
      setBranch(entry.branch);
      setInvestor(entry.investor ?? "");
      setClientBranch(entry.client_branch ?? entry.sub_branch_division ?? "");
      setClientSubBranch(entry.client_sub_branch ?? "");
      // Rebuild the cascade's investor from whichever level the entry sits at.
      setDivisionInvestor(entry.client_branch_investor ?? entry.sub_branch_investor ?? "");
      setProject(entry.project ?? "");
      setDocTypes(entry.doc_types);
      setTagsText(entry.tags.join(", "));
      setWhy(entry.why);
    } else {
      setKind(defaultKind);
      setTitle("");
      setBody("");
      setCategory("terminology");
      setScope("global");
      setBranch("");
      setInvestor("");
      setDivisionInvestor("");
      setClientBranch("");
      setClientSubBranch("");
      setProject("");
      setDocTypes([]);
      setTagsText("");
      setWhy("");
    }
  }, [open, entry, defaultKind]);

  const projectOptions = useMemo(
    () =>
      (projects.data ?? []).map((p) => ({
        value: p.id,
        label: p.client_name ? `${p.name} (${p.client_name})` : p.name,
      })),
    [projects.data],
  );
  const investorOptions = useMemo(
    () => (investors.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    [investors.data],
  );
  const divisionOptions = useMemo(
    () => (divisions.data ?? []).map((d) => ({ value: d.id, label: d.display_name })),
    [divisions.data],
  );
  const subDivisionOptions = useMemo(() => {
    const div = (divisions.data ?? []).find((d) => d.id === clientBranch);
    return (div?.sub_branches ?? []).map((sb) => ({ value: sb.id, label: sb.display_name }));
  }, [divisions.data, clientBranch]);
  const isNote = kind === "note";

  const busy =
    createEntry.isPending || updateEntry.isPending || uploadAttachments.isPending;

  function toggleDocType(dt: DocType) {
    setDocTypes((prev) =>
      prev.includes(dt) ? prev.filter((x) => x !== dt) : [...prev, dt],
    );
  }

  async function handleSubmit() {
    setError(null);
    const payload: KnowledgeEntryPayload = {
      kind,
      title: title.trim(),
      body: body.trim(),
      // Notes are free-form — no review category required (backend defaults it).
      category: isNote ? undefined : category,
      scope,
      branch: scope === "branch" ? branch : "",
      investor: scope === "investor" ? investor || null : null,
      client_branch: scope === "division" ? clientBranch || null : null,
      client_sub_branch: scope === "subdivision" ? clientSubBranch || null : null,
      project: scope === "project" ? project || null : null,
      doc_types: isNote ? [] : docTypes,
      tags: tagsText
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      why: why.trim(),
    };
    if (!payload.title || !payload.body) {
      setError("Tytuł i treść są wymagane.");
      return;
    }
    if (scope === "division" && !payload.client_branch) {
      setError("Wybierz oddział klienta.");
      return;
    }
    if (scope === "subdivision" && !payload.client_sub_branch) {
      setError("Wybierz pododdział (rejon) klienta.");
      return;
    }
    try {
      const saved = entry
        ? await updateEntry.mutateAsync({ id: entry.id, ...payload })
        : await createEntry.mutateAsync(payload);
      if (files.length > 0) {
        await uploadAttachments.mutateAsync({ id: saved.id, files });
      }
      onClose();
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { errors?: Record<string, unknown> } } })?.response
          ?.data?.errors;
      setError(
        detail ? JSON.stringify(detail) : "Nie udało się zapisać wpisu. Spróbuj ponownie.",
      );
    }
  }

  const kindLabel =
    kind === "error"
      ? "błąd historyczny"
      : kind === "note"
        ? "notatkę"
        : kind === "false_positive"
          ? "fałszywy alarm"
          : "wytyczną";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={entry ? "Edytuj wpis" : `Dodaj ${kindLabel}`}
      size="lg"
    >
      <div className="space-y-4">
        {/* Rodzaj */}
        <div className="flex gap-2">
          {meta.kinds.map((k) => (
            <button
              key={k.value}
              type="button"
              onClick={() => setKind(k.value as KnowledgeKind)}
              className={`px-3 py-1.5 text-xs border transition-colors ${
                kind === k.value
                  ? "border-[var(--color-primary)] text-[var(--color-primary)] bg-[var(--color-primary-dim)]"
                  : "border-[var(--color-border-strong)] text-[var(--color-text-secondary)]"
              }`}
            >
              {k.label}
            </button>
          ))}
        </div>

        <Input
          label="Tytuł"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={
            kind === "error"
              ? "np. Mapy częściowo w obrysie, częściowo nie"
              : "np. Piszemy „sieć światłowodowa”, nigdy „internetowa”"
          }
        />

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs text-[var(--color-text-muted)]">
              Treść (to dokładnie przeczyta ENG-ARON){" "}
              {kind === "error" ? "— opisz co było źle i jak powinno być" : ""}
            </label>
            <Button
              size="sm"
              variant="ghost"
              type="button"
              loading={draftEntry.isPending}
              onClick={handleDraft}
              title="Wklej luźny opis w pole treści i/lub dodaj screeny niżej, a AI przygotuje z tego gotowy wpis (tytuł, treść, kategorię, typy)."
            >
              ✨ Przygotuj wpis AI
            </Button>
          </div>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onPaste={handlePasteImages}
            rows={6}
            placeholder="Treść wpisu — albo wklej tu luźny opis/uwagę i kliknij „Przygotuj wpis AI”. Screen ze schowka (Ctrl+V) trafi od razu do galerii poniżej."
            className="w-full border border-[var(--color-border-strong)] bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)] focus:border-[var(--color-primary)] outline-none"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {!isNote && (
            <Select
              label="Kategoria"
              value={category}
              onChange={(e) => setCategory(e.target.value as ReviewCategory)}
              options={meta.categories}
            />
          )}
          <Select
            label="Poziom (zakres obowiązywania)"
            value={scope}
            onChange={(e) => setScope(e.target.value as KnowledgeScope)}
            options={meta.scopes}
          />
        </div>

        {scope === "branch" && (
          <Select
            label="Branża"
            value={branch}
            onChange={(e) => setBranch(e.target.value as EngBranch)}
            options={[{ value: "", label: "— wybierz —" }, ...meta.branches]}
          />
        )}
        {scope === "investor" && (
          <SearchableSelect
            label="Inwestor"
            value={investor}
            onChange={(e) => setInvestor(e.target.value)}
            options={investorOptions}
            placeholder="— wybierz inwestora —"
          />
        )}
        {(scope === "division" || scope === "subdivision") && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <SearchableSelect
              label="Inwestor"
              value={divisionInvestor}
              onChange={(e) => {
                setDivisionInvestor(e.target.value);
                setClientBranch("");
                setClientSubBranch("");
              }}
              options={investorOptions}
              placeholder="— wybierz inwestora —"
            />
            <SearchableSelect
              label="Oddział"
              value={clientBranch}
              onChange={(e) => {
                setClientBranch(e.target.value);
                setClientSubBranch("");
              }}
              options={divisionOptions}
              placeholder={divisionInvestor ? "— wybierz oddział —" : "najpierw inwestor"}
            />
            {scope === "subdivision" && (
              <SearchableSelect
                label="Pododdział / Rejon"
                value={clientSubBranch}
                onChange={(e) => setClientSubBranch(e.target.value)}
                options={subDivisionOptions}
                placeholder={clientBranch ? "— wybierz rejon —" : "najpierw oddział"}
              />
            )}
          </div>
        )}
        {scope === "project" && (
          <SearchableSelect
            label="Projekt (przedsięwzięcie)"
            value={project}
            onChange={(e) => setProject(e.target.value)}
            options={projectOptions}
            placeholder="— wybierz projekt —"
          />
        )}

        <div className={isNote ? "hidden" : ""}>
          <label className="block text-xs text-[var(--color-text-muted)] mb-1">
            Typy opracowań (puste = dotyczy wszystkich)
          </label>
          <div className="flex flex-wrap gap-2">
            {meta.doc_types.map((dt) => (
              <button
                key={dt.value}
                type="button"
                onClick={() => toggleDocType(dt.value as DocType)}
                className={`px-2.5 py-1 text-xs border transition-colors ${
                  docTypes.includes(dt.value as DocType)
                    ? "border-[var(--color-primary)] text-[var(--color-primary)] bg-[var(--color-primary-dim)]"
                    : "border-[var(--color-border)] text-[var(--color-text-secondary)]"
                }`}
              >
                {dt.label}
              </button>
            ))}
          </div>
        </div>

        <Input
          label="Tagi (po przecinku)"
          value={tagsText}
          onChange={(e) => setTagsText(e.target.value)}
          placeholder="np. mapy, obrys, orientacja"
        />

        <Input
          label="Uzasadnienie (opcjonalne — skąd ta zasada)"
          value={why}
          onChange={(e) => setWhy(e.target.value)}
          placeholder="np. seria uwag z PB-KPO-OSJAKÓW-4, 07.2026"
        />

        {/* Screeny — istniejące (edycja) + nowe */}
        <div>
          <label className="block text-xs text-[var(--color-text-muted)] mb-1">
            Screeny {kind === "error" ? "(pokaż jak wyglądał błąd)" : "(opcjonalne)"}
          </label>
          {entry && entry.attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {entry.attachments.map((att) => (
                <div key={att.id} className="relative group">
                  {att.file.url ? (
                    <img
                      src={att.file.url}
                      alt={att.caption || att.file.original_filename}
                      className="h-20 w-28 object-cover border border-[var(--color-border)]"
                    />
                  ) : (
                    <div className="h-20 w-28 border border-[var(--color-border)] flex items-center justify-center text-xs text-gray-400">
                      brak podglądu
                    </div>
                  )}
                  <button
                    type="button"
                    title="Usuń screen"
                    onClick={() =>
                      deleteAttachment.mutate({ id: entry.id, attachmentId: att.id })
                    }
                    className="absolute -top-1.5 -right-1.5 hidden group-hover:flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-white text-xs"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          <FileDropZone files={files} onFilesChange={setFiles} multiple accept="image/*" />
        </div>

        {isNote && (
          <p className="text-xs text-[var(--color-text-muted)]">
            Notatka jest widoczna od razu — ENG-ARON traktuje ją jako pomocniczy
            kontekst (miękki), nie jako twardą regułę recenzji.
          </p>
        )}
        {!meta.is_approver && !isNote && (
          <p className="text-xs text-amber-600">
            Twój wpis trafi do akceptacji (CEO / dyrektor techniczny), zanim ENG-ARON
            zacznie go używać.
          </p>
        )}
        {error && <p className="text-xs text-red-600 break-all">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Anuluj
          </Button>
          <Button onClick={handleSubmit} loading={busy}>
            {entry
              ? "Zapisz zmiany"
              : isNote || meta.is_approver
                ? "Dodaj wpis"
                : "Zaproponuj wpis"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
